import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiPublicPayment } from '../../services/public/payment.public.service';
import { submitSignedForm } from '../../scripts/payment.form.submit';

import logo from '../../assets/themes/svg/logo-official.svg';

// Server contract: pay.routes.ts:52-62
interface PaymentDetails {
  invoice_number?: string;
  amount?: number;
  currency?: string;
  expires_at?: string;
  allow_tokenize?: boolean;
  payment_methods?: unknown[];
}

type PageState = 'loading' | 'ready' | 'error' | 'expired' | 'redirecting';

const PaymentPage: React.FunctionComponent = () => {
  const { lang } = StateLanguage();
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [details, setDetails] = useState<PaymentDetails | null>(null);

  const loadDetails = useCallback(async () => {
    if (!token) {
      setState('error');
      return;
    }

    setState('loading');
    try {
      const response = await ApiPublicPayment.getPaymentDetails(token);
      if (response.status === 200 && response.data) {
        // Client-side expiry check: server sent expires_at, check if it's past
        if (response.data.expires_at && new Date(response.data.expires_at) < new Date()) {
          setDetails(response.data);
          setState('expired');
        } else {
          setDetails(response.data);
          setState('ready');
        }
      } else if (response.status === 410) {
        setState('expired');
      } else {
        setState('error');
      }
    } catch {
      setState('error');
    }
  }, [token]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  // Server contract: checkout returns { redirect_url, params?, order_id }
  // PostFinance: redirect_url only (GET redirect)
  // myPOS (legacy): redirect_url + params (POST signed form) — see submitSignedForm
  const handlePay = async () => {
    if (!token) return;

    setState('redirecting');
    try {
      const response = await ApiPublicPayment.checkout(token);
      if (response.status === 200 && response.data?.redirect_url) {
        const params = response.data.params
        const hasParams = params && typeof params === 'object' && Object.keys(params).length > 0

        if (hasParams) {
          submitSignedForm(response.data.redirect_url, params);
        } else {
          window.location.assign(response.data.redirect_url);
        }
        return;
      }

      // No redirect_url — surface the error instead of silently resetting
      console.error('[PaymentPage] checkout response missing redirect_url', response.status, response.data)
      setState('error');
    } catch (err) {
      console.error('[PaymentPage] checkout failed', err)
      setState('error');
    }
  };

  const formatAmount = (amount?: number, currency?: string): string => {
    if (amount == null) return '';
    const cur = (currency ?? 'CHF').toUpperCase();
    return `${cur} ${amount.toFixed(2)}`;
  };

  return (
    <div className="d-flex flex-column flex-root h-100">
      <div className="d-flex flex-column flex-column-fluid">
        <div className="d-flex flex-column flex-column-fluid text-center p-10">
          <div className="cursor-pointer mb-10">
            <img src={logo} alt={lang.labels.digitalAdmin} className="h-75px mb-5" />
          </div>

          {state === 'loading' && (
            <div className="py-20">
              <span className="spinner-border spinner-border-sm text-primary"></span>
            </div>
          )}

          {state === 'error' && (
            <div className="card card-flush mx-auto" style={{ maxWidth: 500 }}>
              <div className="card-body py-15">
                <div className="text-gray-600 fs-5 mb-5">{lang.labels.paymentLinkInvalid}</div>
                <div className="text-gray-500 fs-7">{lang.labels.paymentLinkInvalidHint}</div>
              </div>
            </div>
          )}

          {state === 'expired' && (
            <div className="card card-flush mx-auto" style={{ maxWidth: 500 }}>
              <div className="card-body py-15">
                <div className="mb-5">
                  <span className="svg-icon svg-icon-3x svg-icon-warning">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="10" fill="black" />
                      <rect x="11" y="14" width="7" height="2" rx="1" transform="rotate(-90 11 14)" fill="black" />
                      <rect x="11" y="17" width="2" height="2" rx="1" transform="rotate(-90 11 17)" fill="black" />
                    </svg>
                  </span>
                </div>
                <div className="text-gray-800 fw-bolder fs-4 mb-3">{lang.labels.paymentLinkExpiredTitle}</div>
                <div className="text-gray-600 fs-6 mb-8">{lang.labels.paymentLinkExpiredDescription}</div>
                <a href="https://app.digitaladmin.ch" className="btn btn-primary">
                  {lang.labels.paymentLinkExpiredAction}
                </a>
              </div>
            </div>
          )}

          {state === 'ready' && details && (
            <div className="card card-flush mx-auto" style={{ maxWidth: 500 }}>
              <div className="card-body py-10">
                <h2 className="fw-bolder mb-8">{lang.labels.paymentTitle}</h2>

                <table className="table gs-0 gy-3 m-0 text-start mx-auto" style={{ maxWidth: 350 }}>
                  <tbody>
                    {details.invoice_number && (
                      <tr>
                        <td className="fw-bold text-muted w-150px">{lang.labels.paymentInvoiceNumber}:</td>
                        <td className="fw-bold fs-6">{details.invoice_number}</td>
                      </tr>
                    )}
                    {details.amount != null && (
                      <tr>
                        <td className="fw-bold text-muted w-150px">{lang.labels.paymentAmount}:</td>
                        <td className="fw-bolder fs-4">{formatAmount(details.amount, details.currency)}</td>
                      </tr>
                    )}
                    {details.expires_at && (
                      <tr>
                        <td className="fw-bold text-muted w-150px">{lang.labels.paymentExpiry}:</td>
                        <td className="fw-bold fs-6">{details.expires_at}</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="mt-10">
                  <button
                    className="btn btn-primary btn-lg"
                    type="button"
                    onClick={handlePay}
                  >
                    {lang.labels.paymentPayNow}
                  </button>
                </div>
              </div>
            </div>
          )}

          {state === 'redirecting' && (
            <div className="card card-flush mx-auto" style={{ maxWidth: 500 }}>
              <div className="card-body py-15">
                <span className="spinner-border spinner-border-sm text-primary me-2"></span>
                <span className="text-gray-600 fs-6">{lang.labels.paymentRedirecting}</span>
              </div>
            </div>
          )}
        </div>
        <div className="d-flex flex-center flex-column-auto p-10">
          <div className="d-flex align-items-center fw-bold fs-6">
            <a href="https://digitaladmin.ch" className="text-muted text-hover-primary px-2">{lang.labels.about}</a>
            <a href="mailto:contact@digitaladmin.ch" className="text-muted text-hover-primary px-2">{lang.labels.contact}</a>
            <a href="mailto:support@digitaladmin.ch" className="text-muted text-hover-primary px-2">{lang.labels.support}</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
